/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import React, { useEffect, useState, useMemo } from 'react';
import type { TaxonomyWithCountDto, TaxonomyFilterOptionsDto } from '@shared/types';
import { fetchAllTaxonomyCodesWithCounts, fetchTaxonomyFilterOptions } from '../services/formularyApi';
import { PageHelp } from '../components/layout/PageHelp';
import { Tooltip } from '../components/layout/Tooltip';
import { TaxonomyFormulasModal } from '../components/taxonomy/TaxonomyFormulasModal';

const PAGE_SIZE = 50;

export function TaxonomyPage() {
  const [codes, setCodes] = useState<TaxonomyWithCountDto[]>([]);
  const [filterOpts, setFilterOpts] = useState<TaxonomyFilterOptionsDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [grouping, setGrouping] = useState('');
  const [classification, setClassification] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [section, setSection] = useState('');
  const [page, setPage] = useState(1);

  // Expanded row
  const [expandedSetId, setExpandedSetId] = useState<string | null>(null);

  // "View formulas" modal
  const [viewFormulasTaxonomy, setViewFormulasTaxonomy] = useState<TaxonomyWithCountDto | null>(null);

  async function reloadCounts() {
    try {
      const res = await fetchAllTaxonomyCodesWithCounts({ noCache: true });
      setCodes(res.data);
      // Keep modal's header count in sync with new data
      if (viewFormulasTaxonomy) {
        const updated = res.data.find((c) => c.setId === viewFormulasTaxonomy.setId);
        if (updated) setViewFormulasTaxonomy(updated);
      }
    } catch {
      // Ignore — existing error surface is sufficient
    }
  }

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([fetchAllTaxonomyCodesWithCounts(), fetchTaxonomyFilterOptions()])
      .then(([codesRes, optsRes]) => {
        if (cancelled) return;
        setCodes(codesRes.data);
        setFilterOpts(optsRes.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load taxonomy codes');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Derived classification options based on selected grouping
  const classificationOptions = useMemo(() => {
    if (!grouping) return filterOpts?.classifications ?? [];
    const set = new Set<string>();
    for (const c of codes) {
      if (c.grouping === grouping && c.classification) set.add(c.classification);
    }
    return [...set].sort();
  }, [grouping, codes, filterOpts]);

  // Derived specialization options based on selected grouping + classification
  const specializationOptions = useMemo(() => {
    if (!grouping && !classification) return filterOpts?.specializations ?? [];
    const set = new Set<string>();
    for (const c of codes) {
      if (grouping && c.grouping !== grouping) continue;
      if (classification && c.classification !== classification) continue;
      if (c.specialization) set.add(c.specialization);
    }
    return [...set].sort();
  }, [grouping, classification, codes, filterOpts]);

  const filtered = useMemo(() => {
    const lc = search.toLowerCase();
    return codes.filter((c) => {
      if (status === 'assigned' && c.formulaCount === 0) return false;
      if (status === 'unassigned' && c.formulaCount > 0) return false;
      if (grouping && c.grouping !== grouping) return false;
      if (classification && c.classification !== classification) return false;
      if (specialization && c.specialization !== specialization) return false;
      if (section && c.section !== section) return false;
      if (lc && !c.code.toLowerCase().includes(lc) && !c.displayName.toLowerCase().includes(lc)) return false;
      return true;
    });
  }, [codes, search, status, grouping, classification, specialization, section]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, status, grouping, classification, specialization, section]);

  // Reset dependent filters when parent changes
  useEffect(() => {
    if (classification && !classificationOptions.includes(classification)) {
      setClassification('');
    }
  }, [classificationOptions, classification]);

  useEffect(() => {
    if (specialization && !specializationOptions.includes(specialization)) {
      setSpecialization('');
    }
  }, [specializationOptions, specialization]);

  function clearFilters() {
    setSearch('');
    setStatus('all');
    setGrouping('');
    setClassification('');
    setSpecialization('');
    setSection('');
  }

  const hasFilters = search || status !== 'all' || grouping || classification || specialization || section;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Taxonomy Codes</h2>
        <Tooltip text="Filtered count vs. total taxonomy codes in the system">
          <span className="text-sm text-gray-500 cursor-help">
            {filtered.length} of {codes.length} codes
          </span>
        </Tooltip>
      </div>

      <PageHelp
        title="How to use the Taxonomy Browser"
        steps={[
          'Search by typing a taxonomy code or display name in the search box.',
          'Use the cascading filters (Section → Grouping → Classification → Specialization) to narrow results.',
          'Click a row to expand and see the full Definition and Notes.',
          'Use Previous/Next to page through results (50 per page).',
        ]}
        tips={[
          'Filters cascade — choosing a Section narrows available Grouping options.',
          'Click "Clear filters" to reset all filters at once.',
          'This is a read-only reference. To assign codes, use the Formula Queue.',
        ]}
      />

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'all' | 'assigned' | 'unassigned')}
          className="text-sm border border-gray-200 rounded px-2 py-1.5 font-medium"
        >
          <option value="all">All</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
        </select>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code or name..."
          className="text-sm border border-gray-200 rounded px-2.5 py-1.5 w-56 focus:outline-none focus:ring-2 focus:ring-dds-blue-500 focus:border-transparent"
        />

        <select
          value={section}
          onChange={(e) => setSection(e.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1.5"
        >
          <option value="">All Sections</option>
          {(filterOpts?.sections ?? []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={grouping}
          onChange={(e) => setGrouping(e.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1.5"
        >
          <option value="">All Groupings</option>
          {(filterOpts?.groupings ?? []).map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>

        <select
          value={classification}
          onChange={(e) => setClassification(e.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1.5"
        >
          <option value="">All Classifications</option>
          {classificationOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          value={specialization}
          onChange={(e) => setSpecialization(e.target.value)}
          className="text-sm border border-gray-200 rounded px-2 py-1.5"
        >
          <option value="">All Specializations</option>
          {specializationOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {hasFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Loading taxonomy codes...</div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5 w-32">Code</th>
                  <th className="px-4 py-2.5">Display Name</th>
                  {status === 'assigned' && (
                    <th className="px-4 py-2.5 w-28 text-center">
                      <Tooltip text="Number of formulas assigned to this taxonomy code">
                        <span className="cursor-help">Formulas</span>
                      </Tooltip>
                    </th>
                  )}
                  <th className="px-4 py-2.5">Section</th>
                  <th className="px-4 py-2.5">Grouping</th>
                  <th className="px-4 py-2.5">Classification</th>
                  <th className="px-4 py-2.5">Specialization</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={status === 'assigned' ? 7 : 6} className="px-4 py-8 text-center text-gray-400">
                      {hasFilters ? 'No codes match the current filters' : 'No taxonomy codes found'}
                    </td>
                  </tr>
                ) : (
                  pageItems.map((c) => (
                    <React.Fragment key={c.setId}>
                      <tr
                        onClick={() => setExpandedSetId(expandedSetId === c.setId ? null : c.setId)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-4 py-2.5 font-medium text-dds-blue-500">{c.code}</td>
                        <td className="px-4 py-2.5 text-gray-900">{c.displayName}</td>
                        {status === 'assigned' && (
                          <td className="px-4 py-2.5 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                {c.formulaCount}
                              </span>
                              {c.formulaCount > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewFormulasTaxonomy(c);
                                  }}
                                  className="px-1.5 py-0.5 text-[11px] rounded border border-dds-blue-200 text-dds-blue-600 hover:bg-dds-blue-50 whitespace-nowrap"
                                  title="View and manage the formulas assigned to this taxonomy code"
                                >
                                  View Formulas
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-gray-600">{c.section ?? '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{c.grouping ?? '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{c.classification ?? '—'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{c.specialization ?? '—'}</td>
                      </tr>
                      {expandedSetId === c.setId && (
                        <tr className="bg-blue-50/50">
                          <td colSpan={status === 'assigned' ? 7 : 6} className="px-4 py-3">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              {c.definition && (
                                <div>
                                  <span className="font-medium text-gray-700">Definition: </span>
                                  <span className="text-gray-600">{c.definition}</span>
                                </div>
                              )}
                              {c.notes && (
                                <div>
                                  <span className="font-medium text-gray-700">Notes: </span>
                                  <span className="text-gray-600">{c.notes}</span>
                                </div>
                              )}
                              {!c.definition && !c.notes && (
                                <div className="text-gray-400 col-span-2">No additional details available</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-gray-500">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-3 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-3 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {viewFormulasTaxonomy && (
        <TaxonomyFormulasModal
          taxonomy={viewFormulasTaxonomy}
          onClose={() => setViewFormulasTaxonomy(null)}
          onAssignmentRemoved={reloadCounts}
        />
      )}
    </div>
  );
}
